import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EventItem } from 'queries/events/types';
import { dayjs } from 'lib';
import SectionBox from 'components/SectionBox/SectionBox';
import { ChartContextProvider } from '@shape/lighthouse-components';
import RiskScoreChart from 'components/LineCharts/Risk Score/RiskScoreChart';
import { useFeatureFlag } from '@shape/feature-flag';
import { ChartsArea } from '../ChartsArea';
import { PlotScalingWrapper } from '../components/PlotScalingWrapper';
import { RadialScalingWrapper } from '../components/RadialScalingWrapper';
import { getAlarmsInfoFromEvent } from '../helpers';

type renderChartsProps = {
  assetId: string;
  selectedRow: EventItem;
  charts: string[];
  activeTab: string;
};

export const useRenderCharts = ({ assetId, selectedRow, charts, activeTab }: renderChartsProps) => {
  const scalingComponent = useFeatureFlag('scaling-components');

  const alarmsInfo = useMemo(() => getAlarmsInfoFromEvent(selectedRow), [selectedRow.id]);

  const renderChart = (chart: string) => {
    switch (chart) {
      case 'sensor': {
        return (
          <ChartsArea
            assetId={assetId}
            selectedRow={selectedRow}
            showExpandBtn={false}
            tabs={
              scalingComponent && charts.includes('feature') ? ['sensor', 'feature'] : ['sensor']
            }
            alarmsInfo={alarmsInfo}
            preSelectedTab={activeTab}
          />
        );
      }
      case 'risk': {
        if (!selectedRow.model_name) return null;
        return (
          <ChartContextProvider
            chartId={`event-risk-${selectedRow.model_name}`}
            alarmDateRange={alarmsInfo.alarmDateRange}
            defaultPeriod="alarm"
          >
            <RiskScoreChart
              modelName={selectedRow.model_name}
              hasAlarmPeriod
              alarmsInfo={alarmsInfo}
            />
          </ChartContextProvider>
        );
      }
      case 'radar':
        if (!selectedRow.model_name) return null;
        return (
          <RadialScalingWrapper
            modelName={selectedRow.model_name}
            hasExpandIcon={false}
            alarmDateRange={{
              end: selectedRow.alarm_datetime_end,
              start: selectedRow.alarm_datetime_start,
            }}
          />
        );

      case 'amoeba':
        if (!selectedRow.model_name) return null;
        return (
          <PlotScalingWrapper
            modelName={selectedRow.model_name}
            hasExpandIcon={false}
            alarmDateRange={{
              end: dayjs(selectedRow.alarm_datetime_end),
              start: dayjs(selectedRow.alarm_datetime_start),
            }}
          />
        );
      default:
        return null;
    }
  };
  const { t } = useTranslation();
  return (
    <SectionBox
      sections={charts.filter((chart) => chart !== 'feature')}
      renderSectionContent={renderChart}
      getTitle={(key) => (key !== 'sensor' ? t(`model_page:sections.${key}`) : '')}
    />
  );
};
